// ======================================================================
// FILE:
// src/modules/finance/transactions/contra_voucher/pages/ContraVoucherEditPage.js
//
// MODULE:
// Finance & Accounts Management System (FBAMS)
//
// FEATURE:
// Contra Voucher - Edit
//
// PURPOSE:
// Edit an existing Finance Contra Voucher Draft.
//
// ACCOUNTING:
// From Account -> Credit
// To Account   -> Debit
//
// IMPORTANT:
// - Uses existing Finance shared components
// - Uses existing financeLookupService
// - Uses contraVoucherService
// - No Redux
// - No accounting logic
// - Service owns business validation
// - Route/API owns transaction commit
// - Only DRAFT Contra Vouchers can be edited
// ======================================================================

import React, {
    useEffect,
    useMemo,
    useState,
} from "react";

import {
    useNavigate,
    useParams,
} from "react-router-dom";

import AsyncSelect
    from "react-select/async";

import FinancePageHeader
    from "../../../components/FinancePageHeader";

import contraVoucherService
    from "../services/contraVoucherService";

import financeLookupService
    from "../../../services/financeLookupService";


// ======================================================================
// INITIAL FORM
// ======================================================================

const getInitialForm = () => ({

    company_id: null,
    company_option: null,

    voucher_date:
        new Date()
            .toISOString()
            .slice(0, 10),

    from_account_id: null,
    from_account_option: null,

    to_account_id: null,
    to_account_option: null,

    currency_id: null,
    currency_option: null,

    exchange_rate: "1",

    reference_no: "",

    reference_date: "",

    remarks: "",

});


// ======================================================================
// INITIAL LINE
// ======================================================================

const getInitialLine = (
    sequenceNo = 1
) => ({

    sequence_no:
        sequenceNo,

    transaction_amount:
        "",

    base_amount:
        "",

    cost_center_id:
        null,

    cost_center_option:
        null,

    description:
        "",

});


// ======================================================================
// COMPONENT
// ======================================================================

export default function ContraVoucherEditPage() {

    const navigate =
        useNavigate();

    const {
        id
    } = useParams();


    // ==================================================================
    // STATE
    // ==================================================================

    const [
        formData,
        setFormData
    ] = useState(
        getInitialForm()
    );


    const [
        lines,
        setLines
    ] = useState([
        getInitialLine(1)
    ]);


    const [
        loading,
        setLoading
    ] = useState(true);


    const [
        saving,
        setSaving
    ] = useState(false);


    const [
        errorMessage,
        setErrorMessage
    ] = useState("");


    // ==================================================================
    // OPTION BUILDERS
    // ==================================================================

    function buildCompanyOption(
        voucher
    ) {

        if (
            !voucher?.company_id
        ) {

            return null;

        }


        return {

            value:
                voucher.company_id,

            label:
                voucher.company_name ||
                `Company ${voucher.company_id}`,

        };

    }


    function buildAccountOption(
        accountId,
        accountCode,
        accountName
    ) {

        if (
            !accountId
        ) {

            return null;

        }


        let label;


        if (
            accountName
        ) {

            label =
                `${accountCode || ""}` +
                (
                    accountCode
                        ? " - "
                        : ""
                ) +
                accountName;

        }
        else {

            label =
                accountCode ||
                `Account ${accountId}`;

        }


        return {

            value:
                accountId,

            label:

                label,

        };

    }


    function buildCurrencyOption(
        currencyId,
        currencyCode
    ) {

        if (
            !currencyId
        ) {

            return null;

        }


        return {

            value:
                currencyId,

            label:
                currencyCode ||
                `Currency ${currencyId}`,

        };

    }


    function buildCostCenterOption(
        costCenterId,
        costCenterName
    ) {

        if (
            !costCenterId
        ) {

            return null;

        }


        return {

            value:
                costCenterId,

            label:
                costCenterName ||
                `Cost Centre ${costCenterId}`,

        };

    }


    // ==================================================================
    // LOAD EXISTING VOUCHER
    // ==================================================================

    useEffect(
        () => {

            loadVoucher();

        },
        [
            id
        ]
    );


    async function loadVoucher() {

        try {

            setLoading(
                true
            );

            setErrorMessage(
                ""
            );


            const voucher =
                await contraVoucherService
                    .getContraVoucher(
                        id
                    );


            if (
                !voucher
            ) {

                throw new Error(
                    "Contra Voucher not found."
                );

            }


            // ----------------------------------------------------------
            // ONLY DRAFT CAN BE EDITED
            // ----------------------------------------------------------

            if (
                String(
                    voucher.status ||
                    ""
                ).toUpperCase()
                !==
                "DRAFT"
            ) {

                throw new Error(
                    "Only Draft Contra Vouchers can be edited."
                );

            }


            // ----------------------------------------------------------
            // HEADER
            // ----------------------------------------------------------

            setFormData({

                company_id:
                    voucher.company_id ||
                    null,

                company_option:
                    buildCompanyOption(
                        voucher
                    ),

                voucher_date:
                    voucher.voucher_date
                        ? String(
                            voucher.voucher_date
                        ).slice(
                            0,
                            10
                        )
                        : "",

                from_account_id:
                    voucher.from_account_id ||
                    null,

                from_account_option:
                    buildAccountOption(
                        voucher.from_account_id,
                        voucher.from_account_code,
                        voucher.from_account_name
                    ),

                to_account_id:
                    voucher.to_account_id ||
                    null,

                to_account_option:
                    buildAccountOption(
                        voucher.to_account_id,
                        voucher.to_account_code,
                        voucher.to_account_name
                    ),

                currency_id:
                    voucher.currency_id ||
                    null,

                currency_option:
                    buildCurrencyOption(
                        voucher.currency_id,
                        voucher.currency_code
                    ),

                exchange_rate:
                    voucher.exchange_rate != null
                        ? String(
                            voucher.exchange_rate
                        )
                        : "1",

                reference_no:
                    voucher.reference_no ||
                    "",

                reference_date:
                    voucher.reference_date
                        ? String(
                            voucher.reference_date
                        ).slice(
                            0,
                            10
                        )
                        : "",

                remarks:
                    voucher.remarks ||
                    "",

            });


            // ----------------------------------------------------------
            // LINES
            // ----------------------------------------------------------

            const existingLines =
                Array.isArray(
                    voucher.lines
                )
                    ? voucher.lines
                    : [];


            if (
                existingLines.length
            ) {

                setLines(

                    existingLines.map(
                        (
                            line,
                            index
                        ) => ({

                            sequence_no:
                                line.sequence_no ||
                                index + 1,

                            transaction_amount:
                                line.transaction_amount != null
                                    ? String(
                                        line.transaction_amount
                                    )
                                    : "",

                            base_amount:
                                line.base_amount != null
                                    ? String(
                                        line.base_amount
                                    )
                                    : "",

                            cost_center_id:
                                line.cost_center_id ||
                                null,

                            cost_center_option:
                                buildCostCenterOption(
                                    line.cost_center_id,
                                    line.cost_center_name
                                ),

                            description:
                                line.description ||
                                "",

                        })
                    )

                );

            }
            else {

                setLines([
                    getInitialLine(1)
                ]);

            }

        }
        catch (
            error
        ) {

            console.error(
                "Contra Voucher load failed:",
                error
            );


            setErrorMessage(

                error?.response?.data?.message ||

                error?.response?.data?.error ||

                error?.message ||

                "Failed to load Contra Voucher."

            );

        }
        finally {

            setLoading(
                false
            );

        }

    }


    // ==================================================================
    // COMPANY LOOKUP
    // ==================================================================

    async function loadCompanies(
        inputValue = ""
    ) {

        try {

            const response =
                await financeLookupService
                    .searchCompanies(
                        inputValue
                    );


            return (
                response ||
                []
            ).map(
                company => ({

                    value:
                        company.id,

                    label:
                        company.company_name,

                    data:
                        company,

                })
            );

        }
        catch (
            error
        ) {

            console.error(
                "Company lookup failed:",
                error
            );


            return [];

        }

    }


    // ==================================================================
    // CHART OF ACCOUNT LOOKUP
    // ==================================================================

    async function loadAccounts(
        inputValue = ""
    ) {

        if (
            !formData.company_id
        ) {

            return [];

        }


        try {

            const response =
                await financeLookupService
                    .searchChartOfAccounts(
                        formData.company_id,
                        inputValue,
                        null,
                        20
                    );


            return (
                response ||
                []
            ).map(
                account => ({

                    ...account,

                    value:
                        account.id,

                    label:
                        account.account_name
                            ? (
                                `${account.account_code || ""}` +
                                (
                                    account.account_code
                                        ? " - "
                                        : ""
                                ) +
                                account.account_name
                            )
                            : (
                                account.name ||
                                `Account ${account.id}`
                            ),

                })
            );

        }
        catch (
            error
        ) {

            console.error(
                "Chart of Account lookup failed:",
                error
            );


            return [];

        }

    }


    // ==================================================================
    // CURRENCY LOOKUP
    // ==================================================================

    async function loadCurrencies(
        inputValue = ""
    ) {

        try {

            const response =
                await financeLookupService
                    .searchCurrencies(
                        inputValue
                    );


            return (
                response ||
                []
            ).map(
                currency => ({

                    value:
                        currency.id ||
                        currency.value,

                    label:
                        currency.currency_name ||
                        currency.currency_code ||
                        currency.label,

                    data:
                        currency,

                })
            );

        }
        catch (
            error
        ) {

            console.error(
                "Currency lookup failed:",
                error
            );


            return [];

        }

    }


    // ==================================================================
    // COST CENTRE LOOKUP
    // ==================================================================

    async function loadCostCenters(
        inputValue = ""
    ) {

        if (
            !formData.company_id
        ) {

            return [];

        }


        try {

            const response =
                await financeLookupService
                    .searchCostCenters(
                        formData.company_id,
                        inputValue,
                        20
                    );


            return (
                response ||
                []
            ).map(
                costCenter => ({

                    ...costCenter,

                    value:
                        costCenter.id,

                    label:
                        costCenter.name ||
                        costCenter.cost_center_name ||
                        costCenter.label ||
                        `Cost Centre ${costCenter.id}`,

                })
            );

        }
        catch (
            error
        ) {

            console.error(
                "Cost Centre lookup failed:",
                error
            );


            return [];

        }

    }
        // ==================================================================
    // HEADER FIELD CHANGE
    // ==================================================================

    function handleFieldChange(
        field,
        value
    ) {

        setFormData(
            previous => ({

                ...previous,

                [field]:
                    value,

            })
        );

    }


    // ==================================================================
    // COMPANY CHANGE
    // ==================================================================

    function handleCompanyChange(
        option
    ) {

        setFormData(
            previous => ({

                ...previous,

                company_id:
                    option?.value ||
                    null,

                company_option:
                    option ||
                    null,

                from_account_id:
                    null,

                from_account_option:
                    null,

                to_account_id:
                    null,

                to_account_option:
                    null,

                currency_id:
                    null,

                currency_option:
                    null,

            })
        );

    }


    // ==================================================================
    // FROM ACCOUNT CHANGE
    // ==================================================================

    function handleFromAccountChange(
        option
    ) {

        setFormData(
            previous => ({

                ...previous,

                from_account_id:
                    option?.value ||
                    null,

                from_account_option:
                    option ||
                    null,

            })
        );

    }


    // ==================================================================
    // TO ACCOUNT CHANGE
    // ==================================================================

    function handleToAccountChange(
        option
    ) {

        setFormData(
            previous => ({

                ...previous,

                to_account_id:
                    option?.value ||
                    null,

                to_account_option:
                    option ||
                    null,

            })
        );

    }


    // ==================================================================
    // CURRENCY CHANGE
    // ==================================================================

    function handleCurrencyChange(
        option
    ) {

        setFormData(
            previous => ({

                ...previous,

                currency_id:
                    option?.value ||
                    null,

                currency_option:
                    option ||
                    null,

            })
        );

    }


    // ==================================================================
    // LINE CHANGE
    // ==================================================================

    function handleLineChange(
        index,
        field,
        value
    ) {

        setLines(
            previous => {

                const updated =
                    [...previous];


                updated[index] = {

                    ...updated[index],

                    [field]:
                        value,

                };


                return updated;

            }
        );

    }


    // ==================================================================
    // COST CENTRE CHANGE
    // ==================================================================

    function handleCostCenterChange(
        index,
        option
    ) {

        setLines(
            previous => {

                const updated =
                    [...previous];


                updated[index] = {

                    ...updated[index],

                    cost_center_id:
                        option?.value ||
                        null,

                    cost_center_option:
                        option ||
                        null,

                };


                return updated;

            }
        );

    }


    // ==================================================================
    // ADD LINE
    // ==================================================================

    function handleAddLine() {

        setLines(
            previous => [

                ...previous,

                getInitialLine(
                    previous.length + 1
                ),

            ]
        );

    }


    // ==================================================================
    // REMOVE LINE
    // ==================================================================

    function handleRemoveLine(
        index
    ) {

        setLines(
            previous => {

                if (
                    previous.length <= 1
                ) {

                    return previous;

                }


                const updated =
                    previous.filter(
                        (
                            _,
                            lineIndex
                        ) =>
                            lineIndex !==
                            index
                    );


                return updated.map(
                    (
                        line,
                        lineIndex
                    ) => ({

                        ...line,

                        sequence_no:
                            lineIndex + 1,

                    })
                );

            }
        );

    }


    // ==================================================================
    // CALCULATE LINE BASE AMOUNT
    // ==================================================================

    function calculateLineBaseAmount(
        line
    ) {

        const transactionAmount =
            Number(
                line.transaction_amount ||
                0
            );


        const exchangeRate =
            Number(
                formData.exchange_rate ||
                0
            );


        return (
            transactionAmount *
            exchangeRate
        );

    }


    // ==================================================================
    // TOTAL TRANSACTION AMOUNT
    // ==================================================================

    const totalTransactionAmount =
        useMemo(
            () => {

                return lines.reduce(
                    (
                        total,
                        line
                    ) => {

                        return (
                            total +
                            Number(
                                line.transaction_amount ||
                                0
                            )
                        );

                    },
                    0
                );

            },
            [
                lines
            ]
        );


    // ==================================================================
    // TOTAL BASE AMOUNT
    // ==================================================================

    const totalBaseAmount =
        useMemo(
            () => {

                return lines.reduce(
                    (
                        total,
                        line
                    ) => {

                        const baseAmount =
                            line.base_amount !==
                                ""
                                ? Number(
                                    line.base_amount ||
                                    0
                                )
                                : calculateLineBaseAmount(
                                    line
                                );


                        return (
                            total +
                            baseAmount
                        );

                    },
                    0
                );

            },
            [
                lines,
                formData.exchange_rate
            ]
        );


    // ==================================================================
    // FORMAT AMOUNT
    // ==================================================================

    function formatAmount(
        value
    ) {

        return Number(
            value ||
            0
        ).toLocaleString(
            "en-IN",
            {

                minimumFractionDigits:
                    2,

                maximumFractionDigits:
                    2,

            }
        );

    }


    // ==================================================================
    // VALIDATION
    // ==================================================================

    function validateForm() {

        const errors =
            [];


        if (
            !formData.company_id
        ) {

            errors.push(
                "Company is required."
            );

        }


        if (
            !formData.voucher_date
        ) {

            errors.push(
                "Voucher date is required."
            );

        }


        if (
            !formData.from_account_id
        ) {

            errors.push(
                "From Account is required."
            );

        }


        if (
            !formData.to_account_id
        ) {

            errors.push(
                "To Account is required."
            );

        }


        if (
            formData.from_account_id &&
            formData.to_account_id &&
            Number(
                formData.from_account_id
            ) ===
            Number(
                formData.to_account_id
            )
        ) {

            errors.push(
                "From Account and To Account cannot be the same."
            );

        }


        if (
            !formData.currency_id
        ) {

            errors.push(
                "Currency is required."
            );

        }


        const exchangeRate =
            Number(
                formData.exchange_rate
            );


        if (
            !exchangeRate ||
            exchangeRate <= 0
        ) {

            errors.push(
                "Exchange Rate must be greater than zero."
            );

        }


        if (
            !lines.length
        ) {

            errors.push(
                "At least one allocation line is required."
            );

        }


        lines.forEach(
            (
                line,
                index
            ) => {

                const amount =
                    Number(
                        line.transaction_amount ||
                        0
                    );


                if (
                    amount <= 0
                ) {

                    errors.push(
                        `Line ${index + 1}: Amount must be greater than zero.`
                    );

                }

            }
        );


        if (
            lines.length &&
            totalTransactionAmount <= 0
        ) {

            errors.push(
                "Total transaction amount must be greater than zero."
            );

        }


        return errors;

    }


    // ==================================================================
    // BUILD UPDATE PAYLOAD
    // ==================================================================

    function buildPayload() {

        const normalizedLines =
            lines.map(
                (
                    line,
                    index
                ) => {

                    const transactionAmount =
                        Number(
                            line.transaction_amount ||
                            0
                        );


                    const baseAmount =
                        line.base_amount !==
                            ""
                            ? Number(
                                line.base_amount ||
                                0
                            )
                            : (
                                transactionAmount *
                                Number(
                                    formData.exchange_rate ||
                                    1
                                )
                            );


                    return {

                        sequence_no:
                            index + 1,

                        transaction_amount:
                            transactionAmount,

                        base_amount:
                            baseAmount,

                        cost_center_id:
                            line.cost_center_id ||
                            null,

                        description:
                            line.description ||
                            null,

                    };

                }
            );


        return {

            company_id:
                formData.company_id,

            voucher_date:
                formData.voucher_date,

            from_account_id:
                formData.from_account_id,

            to_account_id:
                formData.to_account_id,

            currency_id:
                formData.currency_id,

            exchange_rate:
                Number(
                    formData.exchange_rate ||
                    1
                ),

            transaction_amount:
                totalTransactionAmount,

            base_amount:
                totalBaseAmount,

            reference_no:
                formData.reference_no ||
                null,

            reference_date:
                formData.reference_date ||
                null,

            remarks:
                formData.remarks ||
                null,

            lines:
                normalizedLines,

        };

    }


    // ==================================================================
    // UPDATE DRAFT
    // ==================================================================

    async function handleUpdateDraft() {

        const errors =
            validateForm();


        if (
            errors.length
        ) {

            setErrorMessage(
                errors.join(" ")
            );

            return;

        }


        try {

            setSaving(
                true
            );

            setErrorMessage(
                ""
            );


            const payload =
                buildPayload();


            await contraVoucherService
                .updateContraVoucher(
                    id,
                    payload
                );


            navigate(
                `/finance/contra-vouchers/view/${id}`
            );

        }
        catch (
            error
        ) {

            console.error(
                "Contra Voucher update failed:",
                error
            );


            setErrorMessage(

                error?.response?.data?.message ||

                error?.response?.data?.error ||

                error?.message ||

                "Failed to update Contra Voucher."

            );

        }
        finally {

            setSaving(
                false
            );

        }

    }


    // ==================================================================
    // CANCEL
    // ==================================================================

    function handleCancel() {

        navigate(
            `/finance/contra-vouchers/view/${id}`
        );

    }


    // ==================================================================
    // LOADING
    // ==================================================================

    if (
        loading
    ) {

        return (

            <div
                className="
                    d-flex
                    justify-content-center
                    align-items-center
                    py-5
                "
            >

                <div
                    className="
                        spinner-border
                        text-primary
                    "
                    role="status"
                />

            </div>

        );

    }


    // ==================================================================
    // RENDER
    // ==================================================================

    return (

        <>

            {/* ==========================================================
                PAGE HEADER
            ========================================================== */}

            <FinancePageHeader

                icon="fas fa-exchange-alt"

                title="Edit Contra Voucher"

                subtitle={
                    "Edit a Finance Contra Voucher."
                }

                breadcrumbs={[
                    {
                        label:
                            "Finance",

                        to:
                            "/finance",
                    },

                    {
                        label:
                            "Contra Voucher",

                        to:
                            "/finance/contra-vouchers",
                    },

                    {
                        label:
                            "Edit",
                    },
                ]}

                actions={

                    <div
                        className="
                            d-flex
                            gap-2
                        "
                    >

                        <button

                            type="button"

                            className="
                                btn
                                btn-outline-secondary
                            "

                            onClick={
                                handleCancel
                            }

                            disabled={
                                saving
                            }

                        >

                            <i
                                className="
                                    fas
                                    fa-arrow-left
                                    me-1
                                "
                            />

                            Cancel

                        </button>


                        <button

                            type="button"

                            className="
                                btn
                                btn-primary
                            "

                            onClick={
                                handleUpdateDraft
                            }

                            disabled={
                                saving
                            }

                        >

                            {
                                saving
                                    ? (

                                        <>

                                            <span
                                                className="
                                                    spinner-border
                                                    spinner-border-sm
                                                    me-1
                                                "
                                            />

                                            Updating...

                                        </>

                                    )
                                    : (

                                        <>

                                            <i
                                                className="
                                                    fas
                                                    fa-save
                                                    me-1
                                                "
                                            />

                                            Update Draft

                                        </>

                                    )
                            }

                        </button>

                    </div>

                }

            />


            {/* ==========================================================
                ERROR
            ========================================================== */}

            {
                errorMessage && (

                    <div
                        className="
                            alert
                            alert-danger
                            mt-3
                        "
                    >

                        <i
                            className="
                                fas
                                fa-exclamation-triangle
                                me-2
                            "
                        />

                        {errorMessage}

                    </div>

                )
            }


            {/* ==========================================================
                FORM
            ========================================================== */}

            <div
                className="
                    card
                    shadow-sm
                    border-0
                    mt-3
                "
            >

                <div
                    className="
                        card-header
                        fw-semibold
                    "
                >

                    <i
                        className="
                            fas
                            fa-file-invoice
                            me-2
                        "
                    />

                    Contra Voucher Details

                </div>


                <div
                    className="card-body"
                >

                    <div
                        className="row g-3"
                    >

                        {/* ==================================================
                            COMPANY
                        ================================================== */}

                        <div
                            className="
                                col-lg-4
                                col-md-6
                            "
                        >

                            <label
                                className="form-label"
                            >

                                Company
                                <span
                                    className="text-danger"
                                >
                                    *
                                </span>

                            </label>


                            <AsyncSelect

                                cacheOptions

                                defaultOptions

                                loadOptions={
                                    loadCompanies
                                }

                                value={
                                    formData.company_option
                                }

                                onChange={
                                    handleCompanyChange
                                }

                                isDisabled={
                                    true
                                }

                                isClearable={
                                    false
                                }

                                placeholder={
                                    "Company"
                                }

                            />

                        </div>


                        {/* ==================================================
                            VOUCHER DATE
                        ================================================== */}

                        <div
                            className="
                                col-lg-4
                                col-md-6
                            "
                        >

                            <label
                                className="form-label"
                            >

                                Voucher Date
                                <span
                                    className="text-danger"
                                >
                                    *
                                </span>

                            </label>


                            <input

                                type="date"

                                className="
                                    form-control
                                "

                                value={
                                    formData.voucher_date
                                }

                                onChange={
                                    event =>
                                        handleFieldChange(
                                            "voucher_date",
                                            event.target.value
                                        )
                                }

                                disabled={
                                    saving
                                }

                            />

                        </div>


                        {/* ==================================================
                            FROM ACCOUNT
                        ================================================== */}

                        <div
                            className="
                                col-lg-4
                                col-md-6
                            "
                        >

                            <label
                                className="form-label"
                            >

                                From Account
                                <span
                                    className="text-danger"
                                >
                                    *
                                </span>

                            </label>


                            <AsyncSelect

                                cacheOptions

                                defaultOptions

                                loadOptions={
                                    loadAccounts
                                }

                                value={
                                    formData.from_account_option
                                }

                                onChange={
                                    handleFromAccountChange
                                }

                                isDisabled={
                                    saving
                                }

                                isClearable

                                placeholder={
                                    "Select From Account"
                                }

                            />

                        </div>


                        {/* ==================================================
                            TO ACCOUNT
                        ================================================== */}

                        <div
                            className="
                                col-lg-4
                                col-md-6
                            "
                        >

                            <label
                                className="form-label"
                            >

                                To Account
                                <span
                                    className="text-danger"
                                >
                                    *
                                </span>

                            </label>


                            <AsyncSelect

                                cacheOptions

                                defaultOptions

                                loadOptions={
                                    loadAccounts
                                }

                                value={
                                    formData.to_account_option
                                }

                                onChange={
                                    handleToAccountChange
                                }

                                isDisabled={
                                    saving
                                }

                                isClearable

                                placeholder={
                                    "Select To Account"
                                }

                            />

                        </div>


                        {/* ==================================================
                            CURRENCY
                        ================================================== */}

                        <div
                            className="
                                col-lg-4
                                col-md-6
                            "
                        >

                            <label
                                className="form-label"
                            >

                                Currency
                                <span
                                    className="text-danger"
                                >
                                    *
                                </span>

                            </label>


                            <AsyncSelect

                                cacheOptions

                                defaultOptions

                                loadOptions={
                                    loadCurrencies
                                }

                                value={
                                    formData.currency_option
                                }

                                onChange={
                                    handleCurrencyChange
                                }

                                isDisabled={
                                    saving
                                }

                                isClearable

                                placeholder={
                                    "Select Currency"
                                }

                            />

                        </div>


                        {/* ==================================================
                            EXCHANGE RATE
                        ================================================== */}

                        <div
                            className="
                                col-lg-4
                                col-md-6
                            "
                        >

                            <label
                                className="form-label"
                            >

                                Exchange Rate
                                <span
                                    className="text-danger"
                                >
                                    *
                                </span>

                            </label>


                            <input

                                type="number"

                                min="0"

                                step="0.00000001"

                                className="
                                    form-control
                                "

                                value={
                                    formData.exchange_rate
                                }

                                onChange={
                                    event =>
                                        handleFieldChange(
                                            "exchange_rate",
                                            event.target.value
                                        )
                                }

                                disabled={
                                    saving
                                }

                            />

                        </div>


                        {/* ==================================================
                            REFERENCE NO
                        ================================================== */}

                        <div
                            className="
                                col-lg-4
                                col-md-6
                            "
                        >

                            <label
                                className="form-label"
                            >
                                Reference No.
                            </label>


                            <input

                                type="text"

                                className="
                                    form-control
                                "

                                value={
                                    formData.reference_no
                                }

                                onChange={
                                    event =>
                                        handleFieldChange(
                                            "reference_no",
                                            event.target.value
                                        )
                                }

                                disabled={
                                    saving
                                }

                            />

                        </div>


                        {/* ==================================================
                            REFERENCE DATE
                        ================================================== */}

                        <div
                            className="
                                col-lg-4
                                col-md-6
                            "
                        >

                            <label
                                className="form-label"
                            >
                                Reference Date
                            </label>


                            <input

                                type="date"

                                className="
                                    form-control
                                "

                                value={
                                    formData.reference_date
                                }

                                onChange={
                                    event =>
                                        handleFieldChange(
                                            "reference_date",
                                            event.target.value
                                        )
                                }

                                disabled={
                                    saving
                                }

                            />

                        </div>


                        {/* ==================================================
                            REMARKS
                        ================================================== */}

                        <div
                            className="col-12"
                        >

                            <label
                                className="form-label"
                            >
                                Remarks
                            </label>


                            <textarea

                                className="
                                    form-control
                                "

                                rows="3"

                                value={
                                    formData.remarks
                                }

                                onChange={
                                    event =>
                                        handleFieldChange(
                                            "remarks",
                                            event.target.value
                                        )
                                }

                                disabled={
                                    saving
                                }

                            />

                        </div>

                    </div>

                </div>

            </div>
                        {/* ==========================================================
                ALLOCATION LINES
            ========================================================== */}

            <div
                className="
                    card
                    shadow-sm
                    border-0
                    mt-3
                "
            >

                <div
                    className="
                        card-header
                        d-flex
                        justify-content-between
                        align-items-center
                    "
                >

                    <div
                        className="fw-semibold"
                    >

                        <i
                            className="
                                fas
                                fa-list
                                me-2
                            "
                        />

                        Allocation Details

                    </div>


                    <button

                        type="button"

                        className="
                            btn
                            btn-sm
                            btn-outline-primary
                        "

                        onClick={
                            handleAddLine
                        }

                        disabled={
                            saving
                        }

                    >

                        <i
                            className="
                                fas
                                fa-plus
                                me-1
                            "
                        />

                        Add Line

                    </button>

                </div>


                <div
                    className="card-body p-0"
                >

                    <div
                        className="table-responsive"
                    >

                        <table
                            className="
                                table
                                table-bordered
                                mb-0
                                align-middle
                            "
                        >

                            <thead
                                className="table-light"
                            >

                                <tr>

                                    <th
                                        style={{
                                            width: "60px"
                                        }}
                                    >
                                        #
                                    </th>

                                    <th
                                        style={{
                                            minWidth: "160px"
                                        }}
                                    >
                                        Amount
                                        <span
                                            className="text-danger"
                                        >
                                            *
                                        </span>
                                    </th>

                                    <th
                                        style={{
                                            minWidth: "190px"
                                        }}
                                    >
                                        Cost Centre
                                    </th>

                                    <th
                                        style={{
                                            minWidth: "260px"
                                        }}
                                    >
                                        Description
                                    </th>

                                    <th
                                        style={{
                                            width: "80px"
                                        }}
                                    >
                                        Action
                                    </th>

                                </tr>

                            </thead>


                            <tbody>

                                {
                                    lines.map(
                                        (
                                            line,
                                            index
                                        ) => (

                                            <tr
                                                key={
                                                    index
                                                }
                                            >

                                                {/* ==========================
                                                    SEQUENCE
                                                ========================== */}

                                                <td
                                                    className="
                                                        text-center
                                                    "
                                                >

                                                    {
                                                        index + 1
                                                    }

                                                </td>


                                                {/* ==========================
                                                    AMOUNT
                                                ========================== */}

                                                <td>

                                                    <input

                                                        type="number"

                                                        min="0"

                                                        step="0.01"

                                                        className="
                                                            form-control
                                                        "

                                                        value={
                                                            line.transaction_amount
                                                        }

                                                        onChange={
                                                            event =>
                                                                handleLineChange(
                                                                    index,
                                                                    "transaction_amount",
                                                                    event.target.value
                                                                )
                                                        }

                                                        disabled={
                                                            saving
                                                        }

                                                        placeholder="0.00"

                                                    />

                                                </td>


                                                {/* ==========================
                                                    COST CENTRE
                                                ========================== */}

                                                <td>

                                                    <AsyncSelect

                                                        cacheOptions

                                                        defaultOptions

                                                        loadOptions={
                                                            loadCostCenters
                                                        }

                                                        value={
                                                            line.cost_center_option
                                                        }

                                                        onChange={
                                                            option =>
                                                                handleCostCenterChange(
                                                                    index,
                                                                    option
                                                                )
                                                        }

                                                        isDisabled={
                                                            saving
                                                        }

                                                        isClearable

                                                        placeholder={
                                                            "Select Cost Centre"
                                                        }

                                                    />

                                                </td>


                                                {/* ==========================
                                                    DESCRIPTION
                                                ========================== */}

                                                <td>

                                                    <input

                                                        type="text"

                                                        className="
                                                            form-control
                                                        "

                                                        value={
                                                            line.description
                                                        }

                                                        onChange={
                                                            event =>
                                                                handleLineChange(
                                                                    index,
                                                                    "description",
                                                                    event.target.value
                                                                )
                                                        }

                                                        disabled={
                                                            saving
                                                        }

                                                        placeholder={
                                                            "Description"
                                                        }

                                                    />

                                                </td>


                                                {/* ==========================
                                                    REMOVE
                                                ========================== */}

                                                <td
                                                    className="
                                                        text-center
                                                    "
                                                >

                                                    <button

                                                        type="button"

                                                        className="
                                                            btn
                                                            btn-sm
                                                            btn-outline-danger
                                                        "

                                                        onClick={() =>
                                                            handleRemoveLine(
                                                                index
                                                            )
                                                        }

                                                        disabled={
                                                            saving ||
                                                            lines.length <= 1
                                                        }

                                                        title={
                                                            "Remove line"
                                                        }

                                                    >

                                                        <i
                                                            className="
                                                                fas
                                                                fa-trash
                                                            "
                                                        />

                                                    </button>

                                                </td>

                                            </tr>

                                        )
                                    )
                                }

                            </tbody>


                            {/* ==================================================
                                TOTALS
                            ================================================== */}

                            <tfoot
                                className="table-light"
                            >

                                <tr>

                                    <th
                                        colSpan={1}
                                        className="text-end"
                                    >
                                        Total
                                    </th>

                                    <th>

                                        <div
                                            className="
                                                text-end
                                                fw-semibold
                                            "
                                        >

                                            {
                                                formatAmount(
                                                    totalTransactionAmount
                                                )
                                            }

                                        </div>

                                    </th>

                                    <th
                                        colSpan={3}
                                    />

                                </tr>

                            </tfoot>

                        </table>

                    </div>

                </div>

            </div>


            {/* ==========================================================
                ACCOUNTING SUMMARY
            ========================================================== */}

            <div
                className="
                    card
                    shadow-sm
                    border-0
                    mt-3
                "
            >

                <div
                    className="
                        card-header
                        fw-semibold
                    "
                >

                    <i
                        className="
                            fas
                            fa-calculator
                            me-2
                        "
                    />

                    Accounting Summary

                </div>


                <div
                    className="card-body"
                >

                    <div
                        className="row g-3"
                    >

                        {/* ==================================================
                            FROM ACCOUNT
                        ================================================== */}

                        <div
                            className="
                                col-lg-4
                                col-md-6
                            "
                        >

                            <div
                                className="
                                    text-muted
                                    small
                                "
                            >
                                From Account
                            </div>

                            <div
                                className="fw-semibold"
                            >

                                {
                                    formData
                                        .from_account_option
                                        ?.label ||
                                    "-"
                                }

                            </div>

                            <div
                                className="
                                    text-muted
                                    small
                                    mt-1
                                "
                            >

                                Credit

                            </div>

                        </div>


                        {/* ==================================================
                            TO ACCOUNT
                        ================================================== */}

                        <div
                            className="
                                col-lg-4
                                col-md-6
                            "
                        >

                            <div
                                className="
                                    text-muted
                                    small
                                "
                            >
                                To Account
                            </div>

                            <div
                                className="fw-semibold"
                            >

                                {
                                    formData
                                        .to_account_option
                                        ?.label ||
                                    "-"
                                }

                            </div>

                            <div
                                className="
                                    text-muted
                                    small
                                    mt-1
                                "
                            >

                                Debit

                            </div>

                        </div>


                        {/* ==================================================
                            TOTAL
                        ================================================== */}

                        <div
                            className="
                                col-lg-4
                                col-md-12
                            "
                        >

                            <div
                                className="
                                    text-muted
                                    small
                                "
                            >
                                Total Amount
                            </div>

                            <div
                                className="
                                    fw-bold
                                    fs-5
                                "
                            >

                                {
                                    formatAmount(
                                        totalTransactionAmount
                                    )
                                }

                            </div>

                            {
                                formData.currency_option && (

                                    <div
                                        className="
                                            text-muted
                                            small
                                        "
                                    >

                                        {
                                            formData
                                                .currency_option
                                                .label
                                        }

                                    </div>

                                )
                            }

                        </div>

                    </div>

                </div>

            </div>


            {/* ==========================================================
                BOTTOM ACTIONS
            ========================================================== */}

            <div
                className="
                    d-flex
                    justify-content-end
                    gap-2
                    mt-3
                    mb-4
                "
            >

                <button

                    type="button"

                    className="
                        btn
                        btn-outline-secondary
                    "

                    onClick={
                        handleCancel
                    }

                    disabled={
                        saving
                    }

                >

                    <i
                        className="
                            fas
                            fa-times
                            me-1
                        "
                    />

                    Cancel

                </button>


                <button

                    type="button"

                    className="
                        btn
                        btn-primary
                    "

                    onClick={
                        handleUpdateDraft
                    }

                    disabled={
                        saving
                    }

                >

                    {
                        saving
                            ? (

                                <>

                                    <span
                                        className="
                                            spinner-border
                                            spinner-border-sm
                                            me-1
                                        "
                                    />

                                    Updating...

                                </>

                            )
                            : (

                                <>

                                    <i
                                        className="
                                            fas
                                            fa-save
                                            me-1
                                        "
                                    />

                                    Update Draft

                                </>

                            )
                    }

                </button>

            </div>

        </>

    );

}